import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API } from '@/App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle, CardFooter } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { toast } from 'sonner';
import { Loader2, Smartphone, ArrowLeft, KeyRound, Building2, RefreshCw } from 'lucide-react';
import { useLanguage } from '@/contexts/LanguageContext';
import { useFeatureGating } from '@/contexts/FeatureGatingContext';
import { getErrorMessage } from '@/utils/errorUtils';

export default function MobileOtpLogin({ onBackToEmail, onSuccess }) {
  const { t } = useLanguage();
  const { refreshFeatureAccess } = useFeatureGating();
  const navigate = useNavigate();
  
  const [step, setStep] = useState('mobile'); // mobile, otp
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  
  // Form state
  const [mobileNumber, setMobileNumber] = useState('');
  const [hotelCode, setHotelCode] = useState('');
  const [otp, setOtp] = useState('');
  const [requestId, setRequestId] = useState('');
  const [verificationId, setVerificationId] = useState('');
  
  // Resend cooldown
  const [resendCooldown, setResendCooldown] = useState(0);
  
  // Format mobile number as user types
  const handleMobileChange = (e) => {
    let value = e.target.value.replace(/\D/g, ''); // Only digits
    if (value.length > 10) value = value.slice(0, 10);
    setMobileNumber(value);
    setErrorMessage('');
  };
  
  // Format OTP as user types
  const handleOtpChange = (e) => {
    let value = e.target.value.replace(/\D/g, '');
    if (value.length > 6) value = value.slice(0, 6);
    setOtp(value);
    setErrorMessage('');
  };
  
  // Cooldown timer
  useEffect(() => {
    if (resendCooldown > 0) {
      const timer = setTimeout(() => setResendCooldown(resendCooldown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [resendCooldown]);
  
  // Request OTP
  const handleRequestOtp = async (e) => {
    e?.preventDefault();
    setErrorMessage('');
    
    if (mobileNumber.length !== 10) {
      setErrorMessage(t('auth.mobileOtp.invalidMobile'));
      toast.error(t('auth.mobileOtp.invalidMobile'));
      return;
    }
    
    if (!hotelCode.trim()) {
      setErrorMessage(t('auth.mobileOtp.hotelCodePlaceholder'));
      toast.error(t('auth.mobileOtp.hotelCodePlaceholder'));
      return;
    }
    
    setLoading(true);
    try {
      const response = await axios.post(`${API}/auth/mobile/request-otp`, {
        mobile_number: `+91${mobileNumber}`,
        purpose: 'login',
        hotel_id: hotelCode.trim().toUpperCase()
      });
      
      if (response.data.success) {
        setRequestId(response.data.request_id);
        setStep('otp');
        setResendCooldown(response.data.resend_available_in || 60);
        toast.success(t('auth.mobileOtp.otpSent'));
      }
    } catch (error) {
      const msg = getErrorMessage(error, t('auth.mobileOtp.loginFailed'));
      setErrorMessage(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };
  
  // Verify OTP
  const handleVerifyOtp = async (e) => {
    e?.preventDefault();
    
    if (otp.length !== 6) {
      toast.error(t('auth.mobileOtp.invalidOtp'));
      return;
    }
    
    setLoading(true);
    try {
      // Step 1: Verify OTP
      const verifyResponse = await axios.post(`${API}/auth/mobile/verify-otp`, {
        mobile_number: `+91${mobileNumber}`,
        otp: otp,
        purpose: 'login',
        hotel_id: hotelCode.trim().toUpperCase()
      });
      
      if (!verifyResponse.data.success) {
        toast.error(verifyResponse.data.message || t('auth.mobileOtp.invalidOtp'));
        return;
      }
      
      // Step 2: Complete login
      const loginResponse = await axios.post(`${API}/auth/mobile/login`, {
        mobile_number: `+91${mobileNumber}`,
        hotel_id: hotelCode.trim().toUpperCase(),
        otp_verification_id: verifyResponse.data.verification_id
      });
      
      if (loginResponse.data.success) {
        // Store non-sensitive user info only (auth via httpOnly cookies)
        localStorage.setItem('user', JSON.stringify(loginResponse.data.user));
        
        if (loginResponse.data.is_demo) {
          localStorage.setItem('is_demo', 'true');
        } else {
          localStorage.removeItem('is_demo');
        }
        
        // Refresh feature gating
        refreshFeatureAccess();
        
        toast.success(t('auth.mobileOtp.loginSuccess'));
        
        if (onSuccess) {
          onSuccess(loginResponse.data);
        } else {
          navigate('/');
        }
      }
    } catch (error) {
      toast.error(getErrorMessage(error, t('auth.mobileOtp.loginFailed')));
    } finally {
      setLoading(false);
    }
  };
  
  // Resend OTP
  const handleResendOtp = async () => {
    if (resendCooldown > 0) return;
    
    setLoading(true);
    try {
      const response = await axios.post(`${API}/auth/mobile/resend-otp`, {
        mobile_number: `+91${mobileNumber}`,
        purpose: 'login',
        hotel_id: hotelCode.trim().toUpperCase()
      });
      
      if (response.data.success) {
        setRequestId(response.data.request_id);
        setResendCooldown(response.data.resend_available_in || 60);
        setOtp('');
        toast.success(t('auth.mobileOtp.otpSent'));
      }
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to resend OTP'));
    } finally {
      setLoading(false);
    }
  };
  
  // Back to mobile input
  const handleBackToMobile = () => {
    setStep('mobile');
    setOtp('');
    setVerificationId('');
  };
  
  return (
    <div className="space-y-4">
      {/* Error Message Display */}
      {errorMessage && (
        <div 
          className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm flex items-center gap-2"
          role="alert"
          data-testid="mobile-login-error"
        >
          <svg className="w-5 h-5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
          </svg>
          <span>{errorMessage}</span>
        </div>
      )}
      
      {step === 'mobile' ? (
        // Step 1: Enter Mobile & Hotel Code
        <form onSubmit={handleRequestOtp} className="space-y-4">
          <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-4 text-center">
            <Smartphone className="w-10 h-10 text-indigo-600 mx-auto mb-2" />
            <p className="text-sm font-medium text-indigo-800">{t('auth.mobileOtp.title')}</p>
            <p className="text-xs text-indigo-600 mt-1">{t('auth.mobileOtp.subtitle')}</p>
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="mobile">{t('auth.mobileOtp.mobileNumber')}</Label>
            <div className="flex">
              <span className="inline-flex items-center px-3 rounded-l-md border border-r-0 border-gray-300 bg-gray-50 text-gray-500 text-sm">
                +91
              </span>
              <Input
                id="mobile"
                type="tel"
                inputMode="numeric"
                maxLength={10}
                placeholder={t('auth.mobileOtp.mobilePlaceholder')}
                value={mobileNumber}
                onChange={handleMobileChange}
                required
                data-testid="mobile-input"
                className="h-11 rounded-l-none"
              />
            </div>
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="hotel-code" className="flex items-center gap-1">
              <Building2 className="w-3.5 h-3.5" />
              {t('auth.mobileOtp.hotelCode')}
            </Label>
            <Input
              id="hotel-code"
              type="text"
              placeholder={t('auth.mobileOtp.hotelCodePlaceholder')}
              value={hotelCode}
              onChange={(e) => setHotelCode(e.target.value.toUpperCase())}
              required
              data-testid="hotel-code-input"
              className="h-11 font-mono"
            />
          </div>
          
          <Button
            type="submit"
            className="w-full h-11 bg-gradient-to-r from-indigo-500 to-purple-600 hover:from-indigo-600 hover:to-purple-700"
            disabled={loading || mobileNumber.length !== 10}
            data-testid="send-otp-button"
          >
            {loading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                {t('auth.mobileOtp.sendingOtp')}
              </>
            ) : (
              <>
                <Smartphone className="mr-2 h-4 w-4" />
                {t('auth.mobileOtp.sendOtp')}
              </>
            )}
          </Button>
          
          <Button
            type="button"
            variant="ghost"
            className="w-full"
            onClick={onBackToEmail}
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            {t('auth.mobileOtp.backToEmail')}
          </Button>
        </form>
      ) : (
        // Step 2: Enter OTP
        <form onSubmit={handleVerifyOtp} className="space-y-4">
          <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-center">
            <KeyRound className="w-10 h-10 text-green-600 mx-auto mb-2" />
            <p className="text-sm font-medium text-green-800">{t('auth.mobileOtp.enterOtp')}</p>
            <p className="text-xs text-green-600 mt-1">
              {t('auth.mobileOtp.otpSent')}: +91****{mobileNumber.slice(-4)}
            </p>
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="otp">{t('auth.mobileOtp.enterOtp')}</Label>
            <Input
              id="otp"
              type="text"
              inputMode="numeric"
              maxLength={6}
              placeholder={t('auth.mobileOtp.otpPlaceholder')}
              value={otp}
              onChange={handleOtpChange}
              required
              autoFocus
              data-testid="otp-input"
              className="h-14 text-center text-2xl tracking-widest font-mono"
            />
          </div>
          
          <Button
            type="submit"
            className="w-full h-11 bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700"
            disabled={loading || otp.length !== 6}
            data-testid="verify-otp-button"
          >
            {loading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                {t('auth.mobileOtp.verifying')}
              </>
            ) : (
              <>
                <KeyRound className="mr-2 h-4 w-4" />
                {t('auth.mobileOtp.verifyOtp')}
              </>
            )}
          </Button>
          
          {/* Resend OTP */}
          <div className="flex items-center justify-center">
            {resendCooldown > 0 ? (
              <span className="text-sm text-gray-500">
                {t('auth.mobileOtp.resendIn')} {resendCooldown} {t('auth.mobileOtp.seconds')}
              </span>
            ) : (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={handleResendOtp}
                disabled={loading}
              >
                <RefreshCw className="mr-2 h-3 w-3" />
                {t('auth.mobileOtp.resendOtp')}
              </Button>
            )}
          </div>
          
          <Button
            type="button"
            variant="ghost"
            className="w-full"
            onClick={handleBackToMobile}
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            {t('common.back')}
          </Button>
        </form>
      )}
    </div>
  );
}
